import React, { useEffect, useRef, useState } from 'react';
import { Btn } from '../chancey/Btn';
import { Icon } from '../chancey/Icons';
import { NumberRow } from '../chancey/NumberRow';
import { safeAreaPadding } from '../layout/safeArea';
import type { ThemePalette, TypographyTokens } from '../../theme/tokens';
import type { ScannedTicket } from '../../hooks/scannedTicketUtils';
import { statusBg, statusFg, statusLabel } from '../../utils/ticketStatus';

/**
 * Bottom-sheet detail view for a scanned ticket. Mirrors RuleDetailSheet's
 * slide-up + backdrop pattern, and owns the destructive-delete UX:
 * one tap reveals an inline confirm row (not a full modal) so the user
 * never loses context about which ticket they're about to delete.
 *
 * Focus moves into the sheet on open and returns to the caller on close —
 * handled by `triggerRef` that the parent passes in (the ticket card the
 * user tapped).
 */
export interface TicketDetailSheetProps {
  ticket: ScannedTicket | null;
  onClose: () => void;
  onDelete: (ticket: ScannedTicket) => void;
  onRefreshResult?: (ticket: ScannedTicket) => Promise<void>;
  refreshing?: boolean;
  refreshError?: string | null;
  c: ThemePalette;
  typ: TypographyTokens;
  /** Element to return focus to after the sheet closes. */
  triggerRef?: React.RefObject<HTMLElement>;
}

function relativeTime(isoDate: string): string {
  try {
    const diffMs = Date.now() - new Date(isoDate).getTime();
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    const years = Math.round(months / 12);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  } catch {
    return isoDate;
  }
}

function sourceLabel(source: ScannedTicket['source'] | undefined): string {
  if (source === 'manual') return 'Typed in by hand';
  if (source === 'ocr') return 'Camera';
  if (source === 'llm') return 'Smart scan';
  return 'Unknown';
}

export const TicketDetailSheet: React.FC<TicketDetailSheetProps> = ({
  ticket,
  onClose,
  onDelete,
  onRefreshResult,
  refreshing = false,
  refreshError,
  c,
  typ,
  triggerRef,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Reset the confirm state when the sheet opens for a new ticket.
  useEffect(() => {
    setConfirmDelete(false);
  }, [ticket?.id]);

  // Move focus into the sheet on open; return it to the trigger on close.
  useEffect(() => {
    if (!ticket) return;
    const prev = document.activeElement as HTMLElement | null;
    // Snapshot the ref to a local variable so the cleanup function uses the
    // node that existed when the effect ran, not whatever the ref points at
    // by the time React invokes the cleanup (the linter rule ref-cleanup
    // catches exactly this race).
    const triggerNode = triggerRef?.current ?? null;
    closeBtnRef.current?.focus({ preventScroll: true });
    return () => {
      if (triggerNode) {
        triggerNode.focus({ preventScroll: true });
      } else {
        prev?.focus({ preventScroll: true });
      }
    };
  }, [ticket, triggerRef]);

  if (!ticket) return null;
  const winningMatch = winningMatchForTicket(ticket);

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end bg-black/45"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <style>{`
        @keyframes ticket-sheet-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-sheet-title"
        className="max-h-[88%] overflow-y-auto rounded-t-[28px]"
        style={{
          ...safeAreaPadding(10, 20, 32, 20),
          background: c.bg,
          animation: 'ticket-sheet-up 0.3s cubic-bezier(.2,.8,.2,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pb-3">
          <div className="h-[5px] w-10 rounded-md" style={{ background: c.line }} />
        </div>

        <div className="mb-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div
              className="mb-1 font-mono text-[11px] tracking-[0.1em]"
              style={{ color: c.inkMuted, fontFamily: typ.mono }}
            >
              #{ticket.id.slice(0, 6).toUpperCase()}
            </div>
            <div
              id="ticket-sheet-title"
              className="text-[22px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Ticket details
            </div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border"
            style={{ background: c.surface, borderColor: c.line }}
            onClick={onClose}
            aria-label="Close ticket details"
          >
            {Icon.close(16, c.inkSoft)}
          </button>
        </div>

        <div
          className="mb-3 rounded-2xl border p-4"
          style={{ background: c.surface, borderColor: c.line }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.08em]"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              Your numbers
            </div>
            <div
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
              style={{
                background: statusBg(ticket.status, c),
                color: statusFg(ticket.status, c),
                fontFamily: typ.body,
              }}
            >
              {statusLabel(ticket.status, ticket.prize)}
            </div>
          </div>
          <NumberRow
            main={ticket.main}
            bonus={ticket.bonus}
            matchedMain={winningMatch?.main}
            matchedBonus={winningMatch?.bonus}
            size={32}
            gap={6}
            c={c}
          />
          {winningMatch && (
            <div className="mt-3 text-[12px] leading-snug" style={{ color: c.win, fontFamily: typ.body }}>
              Checked numbers matched the official draw.
            </div>
          )}
        </div>

        <div
          className="mb-3 rounded-2xl border p-4"
          style={{ background: c.surface, borderColor: c.line }}
        >
          <dl className="flex flex-col gap-2.5 text-[13px]">
            <MetaRow c={c} typ={typ} label="Draw date" value={ticket.drawDate ?? 'Unknown'} />
            <MetaRow c={c} typ={typ} label="Scanned" value={relativeTime(ticket.scannedAt)} />
            {typeof ticket.cost === 'number' && (
              <MetaRow
                c={c}
                typ={typ}
                label="Cost"
                value={`$${ticket.cost.toFixed(2)}`}
              />
            )}
            <MetaRow c={c} typ={typ} label="Source" value={sourceLabel(ticket.source)} />
            {typeof ticket.confidence === 'number' && (
              <MetaRow
                c={c}
                typ={typ}
                label="Scan confidence"
                value={`${Math.round(ticket.confidence * 100)}%`}
              />
            )}
          </dl>
        </div>

        {onRefreshResult && (
          <div className="mb-3">
            <Btn
              c={c}
              typ={typ}
              variant="ghost"
              style={{
                width: '100%',
                padding: '14px',
                borderColor: c.line,
              }}
              disabled={refreshing}
              onClick={() => {
                void onRefreshResult(ticket);
              }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {Icon.refresh(16, 'currentColor')}
                {refreshing ? 'Refreshing results…' : 'Refresh results'}
              </span>
            </Btn>
            {refreshError && (
              <div className="mt-2 text-[12px] leading-snug" style={{ color: c.lucky, fontFamily: typ.body }}>
                {refreshError}
              </div>
            )}
          </div>
        )}

        {!confirmDelete ? (
          <Btn
            c={c}
            typ={typ}
            variant="ghost"
            style={{
              width: '100%',
              padding: '14px',
              color: c.lucky,
              borderColor: c.line,
            }}
            onClick={() => setConfirmDelete(true)}
          >
            Delete ticket
          </Btn>
        ) : (
          <div
            className="rounded-2xl border p-4"
            style={{ background: c.surfaceAlt, borderColor: c.line }}
            role="alertdialog"
            aria-labelledby="ticket-delete-confirm-title"
          >
            <div
              id="ticket-delete-confirm-title"
              className="mb-1 text-[15px] font-semibold"
              style={{ color: c.ink, fontFamily: typ.body }}
            >
              Delete this ticket?
            </div>
            <div
              className="mb-3.5 text-[13px] leading-snug"
              style={{ color: c.inkSoft, fontFamily: typ.body }}
            >
              This only removes the ticket from Chancey. Your paper ticket does not change.
            </div>
            <div className="flex gap-2.5">
              <Btn
                c={c}
                typ={typ}
                variant="ghost"
                style={{ flex: 1, padding: '12px', borderColor: c.line }}
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Btn>
              <Btn
                c={c}
                typ={typ}
                variant="primary"
                style={{ flex: 1, padding: '12px', background: c.lucky, color: '#fff' }}
                onClick={() => onDelete(ticket)}
              >
                Delete
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetaRow: React.FC<{
  c: ThemePalette;
  typ: TypographyTokens;
  label: string;
  value: string;
}> = ({ c, typ, label, value }) => (
  <div className="flex items-center justify-between">
    <dt
      className="text-[12px] font-medium uppercase tracking-[0.08em]"
      style={{ color: c.inkMuted, fontFamily: typ.body }}
    >
      {label}
    </dt>
    <dd
      className="text-[13px] font-medium"
      style={{ color: c.ink, fontFamily: typ.body }}
    >
      {value}
    </dd>
  </div>
);

function winningMatchForTicket(ticket: ScannedTicket): ScannedTicket['match'] | undefined {
  if (ticket.status !== 'jackpot' && ticket.status !== 'won-small') return undefined;
  return ticket.match;
}
